import { useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark } from '@fortawesome/free-solid-svg-icons'

interface LegalModalProps {
  isOpen: boolean
  onClose: () => void
  type: 'terms' | 'privacy'
  content: string
}

// Simple markdown-like rendering for headers and lists
function renderContent(markdown: string) {
  const lines = markdown.split('\n')
  const elements: JSX.Element[] = []
  let key = 0

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    // H1
    if (line.startsWith('# ')) {
      elements.push(<h1 key={key++}>{line.substring(2)}</h1>)
    }
    // H2
    else if (line.startsWith('## ')) {
      elements.push(<h2 key={key++}>{line.substring(3)}</h2>)
    }
    // H3
    else if (line.startsWith('### ')) {
      elements.push(<h3 key={key++}>{line.substring(4)}</h3>)
    }
    // Bold text line (starts with **)
    else if (line.startsWith('**') && line.endsWith('**')) {
      elements.push(<p key={key++}><strong>{line.slice(2, -2)}</strong></p>)
    }
    // List item
    else if (line.startsWith('- ')) {
      const content = line.substring(2)
      // Check for bold within list item
      const boldMatch = content.match(/\*\*(.+?)\*\*/)
      if (boldMatch) {
        const parts = content.split(/\*\*(.+?)\*\*/)
        elements.push(
          <li key={key++}>
            {parts.map((part, idx) =>
              idx % 2 === 1 ? <strong key={idx}>{part}</strong> : part
            )}
          </li>
        )
      } else {
        elements.push(<li key={key++}>{content}</li>)
      }
    }
    // Regular paragraph with potential inline formatting
    else if (line.trim()) {
      // Handle inline bold
      const parts = line.split(/\*\*(.+?)\*\*/)
      if (parts.length > 1) {
        elements.push(
          <p key={key++}>
            {parts.map((part, idx) =>
              idx % 2 === 1 ? <strong key={idx}>{part}</strong> : part
            )}
          </p>
        )
      } else {
        elements.push(<p key={key++}>{line}</p>)
      }
    }
  }

  return elements
}

export default function LegalModal({ isOpen, onClose, type, content }: LegalModalProps) {
  const { t } = useTranslation()
  const modalRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<Element | null>(null)

  // Focus trap + Escape handler
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose()
      return
    }
    if (e.key === 'Tab' && modalRef.current) {
      const focusable = modalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last?.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first?.focus()
      }
    }
  }, [onClose])

  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement
      document.body.style.overflow = 'hidden'
      document.addEventListener('keydown', handleKeyDown)
      // Focus the close button inside modal
      requestAnimationFrame(() => {
        const firstFocusable = modalRef.current?.querySelector<HTMLElement>('button')
        firstFocusable?.focus()
      })
    }
    return () => {
      if (isOpen) {
        document.body.style.overflow = ''
        document.removeEventListener('keydown', handleKeyDown)
        // Return focus to trigger element
        if (triggerRef.current instanceof HTMLElement) {
          triggerRef.current.focus()
        }
      }
    }
  }, [isOpen, handleKeyDown])

  if (!isOpen) return null

  const title = type === 'terms' ? t('legal.termsOfUse') : t('legal.privacyPolicy')
  const titleId = `legal-modal-title-${type}`

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        ref={modalRef}
        className="modal legal-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '800px', maxHeight: '85vh' }}
      >
        <div className="modal-header">
          <h2 id={titleId} style={{ marginBottom: 0 }}>{title}</h2>
          <button className="btn btn-outline btn-icon" onClick={onClose} aria-label={t('common.close')}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        <div className="modal-body legal-content" style={{ overflowY: 'auto', maxHeight: 'calc(85vh - 130px)' }}>
          {renderContent(content)}
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  )
}
